import { unwrap, type Schemas } from '@mrigl/api-client';
import { Alert, Button, SelectField } from '@mrigl/ui';
import { useState } from 'react';
import { api, errorMessage } from '../api.js';
import { Loading } from '../components.js';
import {
  GridBody,
  GridToolbar,
  Pagination,
  SortHeader,
  useGridState,
  usePagedData,
  type ListQuery,
  type Paged,
} from '../grid.js';
import { useI18n } from '../i18n.js';
import { RatingStars } from '../pages/reviews/ReviewsPages.js';

type Feedback = Schemas['PlatformFeedback'];
type FeedbackPage = Schemas['PlatformFeedbackPage'];
type FeedbackQuery = ListQuery<'/operator-api/v1/feedback'>;

const CATEGORIES: readonly Feedback['category'][] = ['suggestion', 'problem', 'other'];

/** Merchants' ratings and messages about mrigl, with the average rating. */
export function OperatorFeedbackPage() {
  const { t, locale, formatDateTime } = useI18n();
  const [summary, setSummary] = useState<FeedbackPage['summary'] | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const grid = useGridState({
    sort: '-createdAt',
    sortable: ['createdAt', 'rating'],
    filters: { status: 'new', category: '', rating: '' },
  });
  const { filters } = grid;
  const key = JSON.stringify([grid.page, grid.pageSize, grid.sort, filters]);
  const { data, loading, error, reload } = usePagedData<Feedback>(
    key,
    async (): Promise<Paged<Feedback>> => {
      const page = await unwrap(
        api.GET('/operator-api/v1/feedback', {
          params: {
            query: {
              page: grid.page,
              pageSize: grid.pageSize,
              sort: grid.sort as NonNullable<FeedbackQuery['sort']>,
              ...(filters.status === '' ? {} : { status: filters.status as Feedback['status'] }),
              ...(filters.category === ''
                ? {}
                : { category: filters.category as Feedback['category'] }),
              ...(filters.rating === '' ? {} : { rating: Number(filters.rating) }),
            },
          },
        }),
      );
      setSummary(page.summary);
      return page;
    },
    grid.setPage,
  );

  async function setStatus(item: Feedback, status: Feedback['status']) {
    setActionError(null);
    try {
      await unwrap(
        api.PATCH('/operator-api/v1/feedback/{feedbackId}', {
          params: { path: { feedbackId: item.feedbackId } },
          body: { status },
        }),
      );
      reload();
    } catch (failure) {
      setActionError(errorMessage(t, failure));
    }
  }

  const average =
    summary?.average === null || summary === null
      ? null
      : new Intl.NumberFormat(locale, {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        }).format(summary.average);
  return (
    <div className="mrigl-stack">
      <h1 className="mrigl-heading">{t('admin.operator.feedback.title')}</h1>
      {summary === null || average === null ? null : (
        <p className="admin-feedback-average">
          <RatingStars
            rating={Math.round(summary.average ?? 0)}
            label={t('admin.reviews.stars', { rating: Math.round(summary.average ?? 0) })}
          />
          {t('admin.operator.feedback.average', { average, count: summary.count })}
        </p>
      )}
      <GridToolbar activeFilters={grid.activeFilters} onReset={grid.reset}>
        <SelectField
          label={t('admin.comments.status')}
          value={filters.status}
          onChange={(event) => {
            grid.setFilter('status', event.target.value);
          }}
          options={[
            { value: '', label: t('admin.comments.filter.allStatuses') },
            { value: 'new', label: t('admin.comments.status.new') },
            { value: 'handled', label: t('admin.comments.status.handled') },
          ]}
        />
        <SelectField
          label={t('admin.feedback.category')}
          value={filters.category}
          onChange={(event) => {
            grid.setFilter('category', event.target.value);
          }}
          options={[
            { value: '', label: t('admin.operator.feedback.allCategories') },
            ...CATEGORIES.map((value) => ({
              value,
              label: t(`admin.feedback.category.${value}`),
            })),
          ]}
        />
        <SelectField
          label={t('admin.reviews.rating')}
          value={filters.rating}
          onChange={(event) => {
            grid.setFilter('rating', event.target.value);
          }}
          options={[
            { value: '', label: t('admin.reviews.filter.allRatings') },
            ...[5, 4, 3, 2, 1].map((value) => ({
              value: String(value),
              label: t('admin.reviews.stars', { rating: value }),
            })),
          ]}
        />
      </GridToolbar>
      {error === null && actionError === null ? null : (
        <Alert tone="error">{actionError ?? error}</Alert>
      )}
      {data === null ? (
        loading ? (
          <Loading />
        ) : null
      ) : (
        <>
          <GridBody
            loading={loading}
            empty={data.items.length === 0}
            filtered={grid.activeFilters > 0}
            emptyText={t('admin.operator.feedback.empty')}
          >
            <table className="mrigl-table">
              <thead>
                <tr>
                  <SortHeader
                    field="createdAt"
                    label={t('admin.reviews.column.date')}
                    sort={grid.sort}
                    onSort={grid.setSort}
                    firstDescending
                  />
                  <SortHeader
                    field="rating"
                    label={t('admin.reviews.rating')}
                    sort={grid.sort}
                    onSort={grid.setSort}
                    firstDescending
                  />
                  <th scope="col">{t('admin.feedback.category')}</th>
                  <th scope="col">{t('admin.comments.column.message')}</th>
                  <th scope="col">{t('admin.operator.feedback.author')}</th>
                  <th scope="col">{t('admin.comments.status')}</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((item) => (
                  <tr key={item.feedbackId}>
                    <td className="admin-nowrap">{formatDateTime(item.createdAt)}</td>
                    <td>
                      <RatingStars
                        rating={item.rating}
                        label={t('admin.reviews.stars', { rating: item.rating })}
                      />
                    </td>
                    <td>{t(`admin.feedback.category.${item.category}`)}</td>
                    <td className="admin-review-cell">
                      <span dir="auto">{item.message}</span>
                      {item.page === null ? null : (
                        <span className="mrigl-muted mrigl-ltr admin-feedback-page">
                          {item.page}
                        </span>
                      )}
                    </td>
                    <td>
                      <span className="mrigl-ltr">{item.author.email ?? item.author.userId}</span>
                      {item.store === null ? null : (
                        <span className="mrigl-muted mrigl-ltr admin-feedback-page">
                          {item.store.slug}
                        </span>
                      )}
                    </td>
                    <td>
                      <span className={`admin-status admin-status--comment-${item.status}`}>
                        {t(`admin.comments.status.${item.status}`)}
                      </span>
                      <Button
                        variant="secondary"
                        className="admin-feedback-toggle"
                        onClick={() => {
                          void setStatus(item, item.status === 'new' ? 'handled' : 'new');
                        }}
                      >
                        {item.status === 'new'
                          ? t('admin.comments.markHandled')
                          : t('admin.comments.reopen')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </GridBody>
          <Pagination
            page={grid.page}
            pageSize={grid.pageSize}
            total={data.total}
            onPage={grid.setPage}
            onPageSize={grid.setPageSize}
          />
        </>
      )}
    </div>
  );
}
